import React, { useState, useCallback } from "react";
import ProgressCircle from "../GMBScoring/ProgressCircle";
import "./StoreLocatorScoring.css";

const IMPORTANCE_ORDER = { Haute: 0, Moyenne: 1, Basse: 2 };
const FREE_CRITERIA_COUNT = 5;
const MAX_SW_ITEMS = 5;

/**
 * Render plain text that may contain **bold** markdown fragments.
 * Splits on **...** and wraps matches in <strong>.
 */
const RichText = ({ text }) => {
  if (!text) return null;
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? <strong key={i}>{part}</strong> : part,
      )}
    </>
  );
};

const SORT_MODES = {
  importance: { label: "Importance", fn: (a, b) => (IMPORTANCE_ORDER[a.importance] ?? 3) - (IMPORTANCE_ORDER[b.importance] ?? 3) },
  scoreAsc: { label: "Score ↑", fn: (a, b) => a.score - b.score },
  scoreDesc: { label: "Score ↓", fn: (a, b) => b.score - a.score },
};

const StoreLocatorScoring = ({ result, hasFullAccess }) => {
  const [sortMode, setSortMode] = useState("importance");

  if (!result) return null;

  const {
    url,
    globalScore,
    summary,
    ogImage,
    timestamp,
    strengths: rawStrengths = [],
    weaknesses: rawWeaknesses = [],
    recommendations = [],
    criteria = [],
    partial = false,
  } = result;

  const strengths = rawStrengths.slice(0, MAX_SW_ITEMS);
  const weaknesses = rawWeaknesses.slice(0, MAX_SW_ITEMS);

  const sortedCriteria = [...criteria].sort(SORT_MODES[sortMode].fn);

  const visibleCriteria = hasFullAccess
    ? sortedCriteria
    : sortedCriteria.slice(0, FREE_CRITERIA_COUNT);

  const lockedCount = sortedCriteria.length - FREE_CRITERIA_COUNT;
  const goodCount = criteria.filter((c) => c.score / c.maxScore >= 0.7).length;
  const badCount = criteria.filter((c) => c.score / c.maxScore < 0.5).length;

  const formattedDate = timestamp
    ? new Date(timestamp).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <div className="sla">
      {formattedDate && (
        <p className="sla-timestamp">Audit réalisé le {formattedDate}</p>
      )}

      {partial && (
        <div className="sla-partial" role="status">
          <svg
            className="sla-partial__icon"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <div>
            <p className="sla-partial__title">Synthèse IA indisponible</p>
            <p className="sla-partial__msg">
              L'analyse IA (forces, faiblesses, recommandations) n'a pas pu
              être finalisée. Votre score global et le détail des critères
              restent disponibles ci-dessous.
            </p>
          </div>
        </div>
      )}

      {/* ── Score ── */}
      <div className="sla-card sla-card--hero">
        <div className="sla-hero">
          <div className="sla-hero__score">
            <ProgressCircle score={globalScore} width={130} />
          </div>
          <div className="sla-hero__body">
            <p className="sla-hero__summary">{summary}</p>
            <div className="sla-hero__meta">
              {ogImage && (
                <OgImagePreview src={ogImage} alt={`Aperçu ${url}`} />
              )}
              <div className="sla-hero__meta-text">
                <a
                  className="sla-hero__url"
                  href={url}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  {url}
                </a>
                <div className="sla-hero__stats">
                  <span className="sla-stat sla-stat--good">
                    {goodCount} OK
                  </span>
                  <span className="sla-stat sla-stat--bad">
                    {badCount} à améliorer
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Aperçu ── */}
      {(strengths.length > 0 || weaknesses.length > 0) && (
        <div className="sla-grid">
          {strengths.length > 0 && (
            <div className="sla-card">
              <div className="sla-card__head">
                <span className="sla-dot sla-dot--green" />
                <h3 className="sla-card__title">Forces</h3>
              </div>
              <ul className="sla-list">
                {strengths.map((s, i) => (
                  <li key={i}>
                    <RichText text={s} />
                  </li>
                ))}
              </ul>
            </div>
          )}
          {weaknesses.length > 0 && (
            <div className="sla-card">
              <div className="sla-card__head">
                <span className="sla-dot sla-dot--red" />
                <h3 className="sla-card__title">Faiblesses</h3>
              </div>
              <ul className="sla-list">
                {weaknesses.map((w, i) => (
                  <li key={i}>
                    <RichText text={w} />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* ── Recommandations ── */}
      {recommendations.length > 0 && (
        <div className="sla-card">
          <div className="sla-card__head">
            <h3 className="sla-card__title">Recommandations</h3>
          </div>
          <div className="sla-reco">
            {recommendations.map((r, i) => (
              <div key={i} className="sla-reco__item">
                <span className="sla-reco__num">{r.priority || i + 1}</span>
                <p className="sla-reco__text">
                  <RichText text={r.text} />
                </p>
              </div>
            ))}
          </div>

          <div className="sla-demo">
            <div className="sla-demo__content">
              <div className="sla-demo__text">
                <span className="sla-demo__badge">Expert SEO local</span>
                <p className="sla-demo__title">Besoin d'aide pour implémenter ces recommandations ?</p>
                <p className="sla-demo__desc">Échangez avec un expert Localranker pour optimiser vos pages locales et booster votre visibilité.</p>
              </div>
              <a
                href="https://localranker.fr/demander-demo?utm_source=localranker.tech&utm_medium=audit-store-locator"
                target="_blank"
                rel="noreferrer noopener"
                className="sla-demo__cta"
              >
                Demander une démo gratuite
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* ── Critères ── */}
      <div className="sla-card sla-card--flush">
        <div className="sla-card__head sla-card__head--between">
          <h3 className="sla-card__title">Détail des critères</h3>
          <div className="sla-sort">
            {Object.entries(SORT_MODES).map(([key, { label }]) => (
              <button
                key={key}
                type="button"
                className={`sla-sort__btn${sortMode === key ? " sla-sort__btn--active" : ""}`}
                onClick={() => setSortMode(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="sla-criteria">
          {visibleCriteria.map((c) => (
            <CriterionRow key={c.id} criterion={c} />
          ))}
        </div>

        {!hasFullAccess && lockedCount > 0 && (
          <div className="sla-gate">
            <p className="sla-gate__text">
              + {lockedCount} critères masqués
            </p>
            <a
              href="https://localranker.fr/demander-demo?utm_source=localranker.tech&utm_medium=audit-store-locator"
              target="_blank"
              rel="noreferrer noopener"
              className="sla-gate__cta"
            >
              Débloquer l'audit complet
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

const OgImagePreview = ({ src, alt }) => {
  const [failed, setFailed] = useState(false);
  const handleError = useCallback(() => setFailed(true), []);
  if (failed || !src) return null;
  return (
    <div className="sla-hero__og">
      <img src={src} alt={alt} onError={handleError} />
    </div>
  );
};

/**
 * Build a short fallback observation from rawData when Mistral didn't provide one.
 * Includes the score and explains why it was given.
 */
function getFallbackObservation(c) {
  const r = c.rawData;
  if (!r) return `Score : ${c.score}/${c.maxScore}.`;

  const prefix = `Score : ${c.score}/${c.maxScore} — `;

  const map = {
    nap: () =>
      r.name
        ? `${prefix}Nom "${r.name}" détecté. Adresse : ${r.address || "non détectée"}. Téléphone : ${r.phone || "non détecté"}. ${!r.sources?.includes("schema") ? "Pas de source Schema.org, ce qui limite le score." : ""}`
        : `${prefix}Aucune information NAP (nom, adresse, téléphone) détectée sur la page.`,
    h1: () => {
      if (!r.h1s?.length) return `${prefix}Aucun H1 détecté. La balise H1 est essentielle pour le SEO local.`;
      const multiWarning = r.multipleH1s ? ` ⚠️ ${r.h1Count} H1 détectés — il ne devrait y en avoir qu'un seul. Les H1 multiples diluent le signal SEO.` : "";
      const cityInfo = r.cityDetected ? ` Ville "${r.cityDetected}" ${r.cityInH1 ? "présente" : "absente"} dans le H1.` : "";
      return `${prefix}H1 principal : "${r.bestH1 || r.h1s[0]}".${multiWarning}${cityInfo}`;
    },
    title: () => {
      if (!r.title) return `${prefix}Aucune balise title détectée.`;
      const lenNote = r.length > 70 ? " Trop long, risque de troncature dans les SERP." : r.length < 30 ? " Trop court pour être optimisé." : "";
      return `${prefix}Balise title : "${r.title}" (${r.length} caractères).${lenNote}`;
    },
    meta_description: () =>
      r.metaDescription
        ? `${prefix}Meta description : "${r.metaDescription.substring(0, 80)}…" (${r.length} car.).${r.length < 120 ? " Un peu court." : r.length > 160 ? " Risque de troncature." : ""}`
        : `${prefix}Aucune meta description détectée.`,
    schema_org: () =>
      r.hasLocalSchema
        ? `${prefix}Données structurées locales détectées${r.matchedByHeuristic ? " (adresse + horaires)" : ""}${r.type ? `, type ${Array.isArray(r.type) ? r.type.join("/") : r.type}` : ""}. Champs requis : ${r.filledRequired?.join(", ") || "aucun"}. Champs optionnels : ${r.filledOptional?.join(", ") || "aucun"}.`
        : `${prefix}Aucune donnée structurée locale détectée (LocalBusiness ou adresse + horaires). Critère essentiel pour le SEO local.`,
    schema_aggregate_rating: () => {
      if (!r.hasAggregateRating) {
        return `${prefix}Aucun aggregateRating détecté. Ajoutez-le dans le JSON-LD (LocalBusiness ou widget d'avis) avec ratingValue et reviewCount.`;
      }
      const count = r.reviewCount || r.ratingCount;
      const loc =
        r.onLocalSchema === false && r.type
          ? ` dans le JSON-LD (${Array.isArray(r.type) ? r.type.join("/") : r.type})`
          : "";
      if (r.ratingValue && count) return `${prefix}aggregateRating présent${loc} : note ${r.ratingValue}, ${count} avis.`;
      if (r.ratingValue) return `${prefix}aggregateRating partiel${loc} : note ${r.ratingValue} sans nombre d'avis.`;
      return `${prefix}aggregateRating partiel${loc} : ${count} avis sans ratingValue.`;
    },
    schema_same_as: () =>
      r.hasSameAs && r.urls?.length
        ? `${prefix}${r.count || r.urls.length} lien(s) sameAs${r.onLocalSchema === false ? " (hors schéma local)" : ""} : ${r.urls.slice(0, 5).join(", ")}.`
        : `${prefix}Aucun lien sameAs détecté. Ajoutez les URLs de vos fiches officielles (Google Business, réseaux sociaux) dans le JSON-LD.`,
    photos: () =>
      `${prefix}${r.count || 0} images détectées, dont ${r.withAlt || 0} avec attribut alt (${r.altRatio || 0}% de couverture). ${r.descriptiveAlts || 0} alt(s) descriptif(s).`,
    cta: () =>
      r.ctaCount > 0
        ? `${prefix}${r.ctaCount} CTA détecté(s) : ${r.ctaItems?.map((i) => i.text).join(", ")}.`
        : `${prefix}Aucun CTA (appel à l'action) détecté. Les CTA sont essentiels pour convertir les visiteurs.`,
    faq: () =>
      r.hasSchemaFAQ || r.hasHTMLFAQ
        ? `${prefix}Section FAQ détectée (${r.questionCount || 0} questions).${r.hasSchemaFAQ ? " Balisage Schema.org FAQ présent." : " Pas de balisage Schema.org FAQ."}`
        : `${prefix}Aucune section FAQ détectée. Une FAQ améliore le contenu et peut générer des featured snippets.`,
    hours: () =>
      r.hasSchemaHours && r.hasVisibleHours
        ? `${prefix}Horaires détectés dans le Schema.org et visibles sur la page.`
        : r.hasSchemaHours
          ? `${prefix}Horaires trouvés en Schema.org mais pas clairement visibles sur la page.`
          : r.hasVisibleHours
            ? `${prefix}Horaires visibles sur la page mais absents du Schema.org.`
            : `${prefix}Aucun horaire détecté sur la page.`,
    reviews: () => {
      const parts = [];
      if (r.hasRating) parts.push(`note ${r.ratingValue}/5`);
      if (r.reviewCount) parts.push(`${r.reviewCount} avis`);
      if (r.hasCarousel) parts.push("carousel d'avis");
      if (r.hasTestimonials) parts.push("section témoignages");
      if (r.hasWidget) parts.push("widget d'avis");
      return parts.length > 0
        ? `${prefix}Éléments détectés : ${parts.join(", ")}.${!r.hasRating ? " Pas de note agrégée Schema.org détectée." : ""}`
        : `${prefix}Aucun avis ou note détecté. La preuve sociale est un levier important pour la conversion.`;
    },
    social_media: () =>
      r.count > 0
        ? `${prefix}${r.count} réseau(x) social(aux) lié(s) : ${Object.keys(r.platforms || {}).join(", ")}.`
        : `${prefix}Aucun lien vers les réseaux sociaux détecté.`,
    service_links: () =>
      r.count > 0
        ? `${prefix}${r.count} lien(s) vers des pages de services/produits détecté(s).`
        : `${prefix}Aucun lien vers des pages dédiées de services ou produits. Ajoutez des liens vers vos pages de services.`,
    services_products: () =>
      `${prefix}${r.matchCount || 0} pattern(s) de contenu services/produits détecté(s).${r.hasProductSchema ? " Schema.org produit/service présent." : ""}${r.h2Mentions?.length > 0 ? ` H2 mentionnant les services : ${r.h2Mentions.join(", ")}.` : ""}`,
    url: () =>
      r.path
        ? `${prefix}URL : ${r.hostname}${r.path}.`
        : `${prefix}Impossible d'analyser l'URL.`,
    mobile: () =>
      `${prefix}${r.viewport?.exists ? "Balise viewport présente." : "Aucune balise viewport — la page n'est probablement pas responsive."}`,
    certifications: () =>
      r.found?.length > 0
        ? `${prefix}Certifications/labels détectés : ${r.found.join(", ")}.`
        : `${prefix}Aucune certification ou label détecté sur la page.`,
    other_locations: () =>
      r.otherLocationLinks?.length > 0
        ? `${prefix}${r.otherLocationLinks.length} lien(s) vers d'autres établissements détecté(s).`
        : `${prefix}Aucun lien vers d'autres établissements. Le maillage inter-localisations aide le SEO.`,
    internal_linking: () =>
      `${prefix}${r.internalLinkCount || 0} lien(s) interne(s) détecté(s).${(r.internalLinkCount || 0) < 5 ? " Le maillage interne est insuffisant." : ""}`,
    value_proposition: () =>
      `${prefix}${r.matchCount || 0} élément(s) de proposition de valeur détecté(s). Longueur du contenu : ${r.textLength || 0} caractères.`,
    logo: () =>
      r.logo?.found
        ? `${prefix}Logo détecté sur la page.`
        : `${prefix}Aucun logo détecté. Le branding est important pour la reconnaissance.`,
    equipment: () =>
      r.keywords?.length > 0
        ? `${prefix}Équipements détectés : ${r.keywords.join(", ")}.`
        : `${prefix}Aucun équipement ou attribut détecté.`,
    opening_date: () =>
      r.found
        ? `${prefix}Date d'ouverture détectée : ${r.context}.`
        : `${prefix}Aucune date d'ouverture détectée.`,
    map_presence: () =>
      r.hasMap
        ? `${prefix}Carte intégrée détectée (${r.provider || "fournisseur inconnu"}).${r.sources?.length ? ` Source : ${r.sources.join(", ")}.` : ""}`
        : `${prefix}Aucune carte / plan intégré détecté. L'intégration d'une carte (Google Maps, Leaflet, Mapbox…) est essentielle pour une page locale.`,
    maps_link: () =>
      r.count > 0
        ? `${prefix}${r.count} lien(s) vers Google Maps ou directions détecté(s).`
        : `${prefix}Aucun lien vers Google Maps/itinéraire détecté.`,
    promotional_content: () => {
      const items = [];
      if (r.hasGBPPosts) items.push("publications Google Business");
      if (r.hasPosts && !r.hasGBPPosts) items.push("articles/posts");
      if (r.hasBlog) items.push("section blog/actualités");
      if (r.hasPromos) items.push("promotions");
      if (r.hasSocialFeed) items.push("publications sociales");
      if (r.postCount > 0) items.push(`${r.postCount} publication(s)`);
      return items.length > 0
        ? `${prefix}Contenu promotionnel détecté : ${items.join(", ")}.`
        : `${prefix}Aucun contenu promotionnel, article ou publication sociale détecté.`;
    },
  };

  return map[c.id]?.() || `Score : ${c.score}/${c.maxScore}.`;
}

const CriterionRow = ({ criterion: c }) => {
  const [open, setOpen] = useState(false);
  const ratio = c.score / c.maxScore;
  const barColor =
    ratio >= 0.8
      ? "var(--sla-green)"
      : ratio >= 0.5
        ? "var(--sla-orange)"
        : "var(--sla-red)";

  const observation = c.observation || getFallbackObservation(c);
  const recommendation = c.recommendation;
  const hasDetail = observation || recommendation;

  return (
    <div className={`sla-cr${open ? " sla-cr--open" : ""}`}>
      <button
        type="button"
        className="sla-cr__row"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="sla-cr__name">{c.name}</span>
        <span
          className={`sla-cr__imp sla-cr__imp--${c.importance.toLowerCase()}`}
        >
          {c.importance}
        </span>
        <span className="sla-cr__bar-wrap">
          <span
            className="sla-cr__bar"
            style={{ width: `${ratio * 100}%`, background: barColor }}
          />
        </span>
        <span className="sla-cr__score">{c.score}</span>
        <span className="sla-cr__chevron" />
      </button>

      {open && hasDetail && (
        <div className="sla-cr__detail">
          {observation && (
            <div className="sla-cr__obs">
              <span className="sla-cr__label">Observation</span>
              <RichText text={observation} />
            </div>
          )}
          {recommendation && (
            <div className="sla-cr__rec">
              <span className="sla-cr__label">Recommandation</span>
              <RichText text={recommendation} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StoreLocatorScoring;
